import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, ChevronRight, ImageIcon, Clock, Dumbbell as DumbbellIcon } from 'lucide-react';
import { api, DAY_NAMES, DAY_SHORT } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';

export default function Home() {
    const [template, setTemplate] = useState(null);
    const [days, setDays] = useState([]);
    const [selectedDayId, setSelectedDayId] = useState(null);
    const [routines, setRoutines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingRoutines, setLoadingRoutines] = useState(false);
    const navigate = useNavigate();

    const today = useMemo(() => {
        // JS: Sunday=0..Saturday=6. We use Monday=1..Sunday=7.
        const j = new Date().getDay();
        return j === 0 ? 7 : j;
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const tRes = await api.get('/templates');
                const active = tRes.data.find((t) => t.is_active) || tRes.data[0];
                setTemplate(active);
                if (active) {
                    const dRes = await api.get(`/templates/${active.id}/days`);
                    setDays(dRes.data);
                    const todayDay = dRes.data.find((d) => d.day_of_week === today) || dRes.data[0];
                    if (todayDay) setSelectedDayId(todayDay.id);
                }
            } finally {
                setLoading(false);
            }
        })();
    }, [today]);

    useEffect(() => {
        if (!selectedDayId) return;
        setLoadingRoutines(true);
        api.get(`/days/${selectedDayId}/routines`)
            .then((r) => setRoutines(r.data))
            .finally(() => setLoadingRoutines(false));
    }, [selectedDayId]);

    const start = () => {
        if (selectedDayId) navigate(`/workout/${selectedDayId}`);
    };

    const selectedDay = days.find((d) => d.id === selectedDayId);

    return (
        <div className="px-4 lg:px-8 py-4 lg:py-8 max-w-[1400px]">
            <header className="mb-6">
                <div className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-xs text-muted-foreground uppercase tracking-widest">Plantilla activa</div>
                        <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight" data-testid="active-template-name">{template?.name || '—'}</h1>
                    </div>
                    <Button size="lg" className="h-12 gap-2" onClick={start} disabled={!selectedDayId || routines.length === 0} data-testid="start-workout-button">
                        <Play className="h-5 w-5" /> Empezar entreno
                    </Button>
                </div>
            </header>

            {/* Day tabs */}
            {loading ? (
                <div className="grid grid-cols-4 md:grid-cols-7 gap-2 mb-6">
                    {Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-xl" />)}
                </div>
            ) : (
                <div className="grid grid-cols-4 md:grid-cols-7 gap-2 bg-card p-2 rounded-[var(--radius)] border border-border mb-6" data-testid="week-day-tabs">
                    {[1, 2, 3, 4, 5, 6, 7].map((dow) => {
                        const d = days.find((x) => x.day_of_week === dow);
                        const active = selectedDayId === d?.id;
                        const disabled = !d;
                        return (
                            <button
                                key={dow}
                                disabled={disabled}
                                onClick={() => d && setSelectedDayId(d.id)}
                                data-testid={`week-day-tab-${DAY_SHORT[dow].toLowerCase()}`}
                                className={`h-14 rounded-[12px] flex flex-col items-center justify-center transition-colors ${
                                    active ? 'bg-[hsl(var(--surface-2))] text-foreground border border-border' : 'text-muted-foreground hover:bg-[hsl(var(--surface-2))]'
                                } ${disabled ? 'opacity-30 cursor-not-allowed' : ''} ${dow === today ? 'ring-1 ring-[hsl(var(--primary))]/40' : ''}`}
                            >
                                <span className="text-[10px] uppercase tracking-widest">{DAY_SHORT[dow]}</span>
                                <span className="text-sm font-medium">{d ? d.name : '—'}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Content - split on iPad landscape */}
            <div className="lg:grid lg:grid-cols-12 lg:gap-6">
                <div className="lg:col-span-12">
                    {selectedDay ? (
                        <Card className="bg-card border-border p-5">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <div className="text-xs text-muted-foreground uppercase tracking-widest">{DAY_NAMES[selectedDay.day_of_week]}</div>
                                    <div className="text-xl font-semibold font-display">{selectedDay.name || DAY_NAMES[selectedDay.day_of_week]}</div>
                                </div>
                                <Badge variant="secondary" className="gap-1">
                                    <Clock className="h-3 w-3" /> {selectedDay.total_duration_min} min
                                </Badge>
                            </div>
                            <ScrollArea className="max-h-[calc(100vh-320px)] scroll-soft" data-testid="day-exercise-list">
                                {loadingRoutines ? (
                                    <div className="space-y-2">
                                        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
                                    </div>
                                ) : routines.length === 0 ? (
                                    <div className="py-10 text-center text-muted-foreground" data-testid="empty-day-exercises">
                                        <DumbbellIcon className="h-10 w-10 mx-auto mb-3 opacity-50" />
                                        <div className="mb-3">Aún no hay ejercicios en este día.</div>
                                        <Button variant="secondary" onClick={() => navigate('/templates')} data-testid="add-exercises-to-day-button">Añadir ejercicios</Button>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {routines.map((r, idx) => (
                                            <div key={r.id} className="flex items-center gap-3 p-3 rounded-[var(--radius)] bg-[hsl(var(--surface-2))] border border-border" data-testid={`exercise-list-item-${r.exercise_id}`}>
                                                <div className="h-14 w-14 rounded-lg overflow-hidden bg-[hsl(var(--surface-3))] border border-border flex-shrink-0 grid place-items-center">
                                                    {r.exercise?.media_url ? (
                                                        <img src={r.exercise.media_url} alt={r.exercise.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                                    ) : (
                                                        <ImageIcon className="h-5 w-5 text-muted-foreground" />
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-medium truncate" data-testid="exercise-list-item-title">{idx + 1}. {r.exercise?.name || 'Ejercicio'}</div>
                                                    <div className="text-xs text-muted-foreground" data-testid="exercise-list-item-meta">
                                                        {r.sets} series · {r.reps_or_time}{r.exercise?.type === 'time' && !String(r.reps_or_time).endsWith('s') ? 's' : ''} · descanso {r.rest_seconds}s
                                                    </div>
                                                </div>
                                                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </ScrollArea>
                        </Card>
                    ) : (
                        <Card className="bg-card border-border p-10 text-center text-muted-foreground" data-testid="no-days">
                            No hay días configurados. Ve a <button className="underline" onClick={() => navigate('/templates')}>Plantillas</button> para crear tu primer día.
                        </Card>
                    )}
                </div>
            </div>
        </div>
    );
}
